<script setup lang="ts">
import { computed } from 'vue';

const DEFAULT_CTA_HREF = '/quiz/weight-loss';
const DEFAULT_CTA_TEXT = 'Start for $89/month';
const DEFAULT_FOOTNOTE = '*Does not include the cost of medication.';

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    bodyHtml?: string;
    imageUrl?: string;
    imageAlt?: string;
    bgColor?: string;
    ctaHref?: string;
    ctaText?: string;
    ctaTarget?: string;
    footnoteHtml?: string;
    quoteHtml?: string;
    attribution?: string;
  }>(),
  {
    headingHtml: '',
    bodyHtml: '',
    imageUrl: '',
    imageAlt: '',
    bgColor: '',
    ctaHref: '',
    ctaText: '',
    ctaTarget: '_self',
    footnoteHtml: '',
    quoteHtml: '',
    attribution: '',
  },
);

const heading = computed(() => {
  const h = strip(props.headingHtml);
  return h || strip(props.quoteHtml) || 'Why JRNYS?';
});

const body = computed(() => {
  const b = strip(props.bodyHtml);
  if (b) return props.bodyHtml!;
  const attr = strip(props.attribution);
  return attr ? props.attribution! : '';
});

const ctaHrefResolved = computed(() => (props.ctaHref || '').trim() || DEFAULT_CTA_HREF);
const ctaTextResolved = computed(() => (props.ctaText || '').trim() || DEFAULT_CTA_TEXT);

const footnote = computed(() => {
  const f = strip(props.footnoteHtml);
  return f ? props.footnoteHtml! : DEFAULT_FOOTNOTE;
});

const cardBg = computed(() => props.bgColor || '#7BA59D');
const hasImage = computed(() => !!(props.imageUrl || '').trim());

function strip(html: string | undefined): string {
  return (html ?? '').replace(/<[^>]*>/g, '').trim();
}
</script>

<template>
  <section class="why-jrnys-section pb-28 pt-16 md:pb-32 md:pt-24 lg:pb-24 lg:pt-64">
    <div class="container">
      <div
        class="relative w-full overflow-hidden rounded-[20px] md:rounded-[28px] lg:h-[480px]"
        :style="{ backgroundColor: cardBg }"
      >
        <!-- Desktop: side-by-side layout -->
        <div class="hidden h-full lg:flex lg:flex-row">
          <div class="flex h-full w-[55%] min-w-0 flex-col justify-between px-64 py-48 font-inter">
            <div>
              <h2
                class="font-inter-tight text-40 font-semibold leading-[1.15] text-white"
                v-html="heading"
              />
              <div
                v-if="body"
                class="why-jrnys-body mt-20 min-w-0 max-w-full !font-inter text-18 font-normal leading-relaxed text-white/90"
                v-html="body"
              />
            </div>
            <div>
              <a
                :href="ctaHrefResolved"
                :target="props.ctaTarget || '_self'"
                class="inline-flex items-center justify-center rounded-full bg-white px-32 py-14 text-15 font-semibold text-deep-blue transition-opacity hover:opacity-90"
              >
                {{ ctaTextResolved }}
              </a>
              <p
                class="mt-10 text-12 text-white/60"
                v-html="footnote"
              />
            </div>
          </div>
          <div
            v-if="hasImage"
            class="relative h-full w-[45%] min-w-0 shrink-0 overflow-hidden"
          >
            <img
              :src="imageUrl"
              :alt="imageAlt || 'Why JRNYS'"
              class="absolute inset-0 size-full object-cover object-bottom"
            >
          </div>
        </div>

        <!-- Mobile: image behind bottom content -->
        <div class="relative lg:hidden">
          <!-- Text content -->
          <div class="relative z-10 px-24 pt-28 md:px-48 md:pt-32">
            <h2
              class="md:text-36 font-inter-tight text-28 font-bold leading-[1.15] text-white"
              v-html="heading"
            />
            <div
              v-if="body"
              class="why-jrnys-body mt-16 font-inter text-18 leading-relaxed text-white/90 md:mt-20"
              v-html="body"
            />
          </div>

          <!-- Image behind CTA area -->
          <div class="relative mt-0 pb-0">
            <img
              v-if="hasImage"
              :src="imageUrl"
              :alt="imageAlt || 'Why JRNYS'"
              class="mx-auto mb-0 block max-h-[240px] w-full object-contain object-bottom sm:max-h-[280px] md:max-h-[300px]"
            >

            <!-- CTA + footnote overlaid on top of image -->
            <div class="absolute inset-x-0 bottom-0 z-10 px-24 pb-16 font-inter md:px-48">
              <a
                :href="ctaHrefResolved"
                :target="props.ctaTarget || '_self'"
                class="flex w-full items-center justify-center rounded-full bg-white px-28 py-14 text-14 font-semibold text-off-black-500 transition-opacity hover:opacity-90 md:text-15"
              >
                {{ ctaTextResolved }}
              </a>
              <p
                class="mt-8 font-inter text-11 text-white opacity-60 md:text-12"
                v-html="footnote"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.why-jrnys-body :deep(p + p) {
  margin-top: 12px;
}

.why-jrnys-body :deep(strong) {
  font-weight: 700;
  color: #fff;
}
</style>
